/**
 * useAuth Hook - React hook wrapper around authentication service
 * Provides authentication functionality in React components
 * Uses TokenManager and AuthService for actual operations
 */

import { useQueryClient } from "@tanstack/react-query";
import { authService, tokenManager } from "../../services/auth";
import { requestPageTransition } from "../../core/pageTransition";

/**
 * useAuth Hook - Authentication context
 * Provides auth functions and state management
 *
 * @returns {Object} Authentication methods and utilities
 */
export const useAuth = () => {
  const queryClient = useQueryClient();
  /**
   * Check if user is authenticated
   * Validates token exists and hasn't expired
   *
   * @returns {boolean} True if user is authenticated
   */
  const auth = () => {
    return tokenManager.hasValidToken();
  };

  /**
   * Login user with email and password
   * Handles 2FA flow and token management
   *
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} Login result
   */
  const login = async (email, password) => {
    try {
      const result = await authService.login(email, password);
      return result;
    } catch (error) {
      console.error("Login hook error:", error);
      return {
        success: false,
        error: error.message || "Login failed",
      };
    }
  };

  /**
   * Logout current user
   * Clears token and calls backend logout
   *
   * @returns {Promise<void>}
   */
  const logout = async () => {
    requestPageTransition();
    await authService.logout();
    queryClient.clear();

    // Small delay to ensure React state updates propagate
    await new Promise((resolve) => setTimeout(resolve, 50));
  };

  /**
   * Get current user profile data
   * Fetches from backend using current token
   *
   * @returns {Promise<Object|null>} User profile or null
   */
  const getUserData = async () => {
    try {
      return await authService.getCurrentUser();
    } catch (error) {
      console.error("Get user data error:", error);
      return null;
    }
  };

  return {
    auth,
    login,
    logout,
    getUserData,
  };
};

export default useAuth;
